<script lang="ts">
	import { onMount } from 'svelte';
	import { appStore } from '../../stores/appStore';
	import { get } from 'svelte/store';
	import type { AdminUserInfo } from '../../api_contracts/app';

	let users: AdminUserInfo[] = [];
	let loading = false;
	let error = '';
	let currentPage = 1;
	let totalPages = 1;
	let totalCount = 0;
	let limit = 20;

	async function fetchUsers(page: number = 1) {
		loading = true;
		error = '';

		try {
			const client = get(appStore).client;
			const token = get(appStore).token;

			if (!client || !token) {
				error = 'Not authenticated';
				return;
			}

			const response = await client.getPaginatedUsers({
				token,
				page,
				limit
			});

			if (response.response.successFull) {
				users = response.response.users || [];
				currentPage = response.response.page || 1;
				totalPages = response.response.totalPages || 1;
				totalCount = response.response.totalCount || 0;
			} else {
				error = response.response.reason || 'Failed to fetch users';
			}
		} catch (e: any) {
			error = e.message || 'An error occurred while fetching users';
			console.error('Failed to fetch users:', e);
		} finally {
			loading = false;
		}
	}

	function goToPage(page: number) {
		if (page < 1 || page > totalPages) return;
		fetchUsers(page);
	}

	onMount(() => {
		fetchUsers();
	});
</script>

<div class="users-container">
	<div class="users-header">
		<h2>Users</h2>
		<p class="total-count">{totalCount} total users</p>
	</div>

	{#if loading}
		<div class="loading">Loading users...</div>
	{:else if error}
		<div class="error">{error}</div>
	{:else}
		<div class="users-table-container">
			<table class="users-table">
				<thead>
					<tr>
						<th>Username</th>
						<th>Name</th>
						<th>Email</th>
						<th>Role</th>
						<th>Status</th>
						<th>Country</th>
						<th>Joined</th>
					</tr>
				</thead>
				<tbody>
					{#each users as user}
						<tr>
							<td>
								<div class="user-cell">
									{#if user.avatarUrl}
										<img src={user.avatarUrl} alt={user.userName} class="user-avatar" />
									{:else}
										<div class="user-avatar-placeholder">
											{user.userName.charAt(0).toUpperCase()}
										</div>
									{/if}
									<span class="username">{user.userName}</span>
								</div>
							</td>
							<td>{user.firstName} {user.lastName}</td>
							<td class="email">{user.email}</td>
							<td>
								<span class="role-badge" class:admin={user.userRole === 'admin'}>
									{user.userRole}
								</span>
							</td>
							<td>
								<span class="status-badge" class:active={user.userStatus === 1}>
									{user.userStatus === 1 ? 'Active' : 'Inactive'}
								</span>
							</td>
							<td>{user.country}</td>
							<td>{new Date(user.createdAt).toLocaleDateString()}</td>
						</tr>
					{/each}
				</tbody>
			</table>
		</div>

		{#if totalPages > 1}
			<div class="pagination">
				<button
					class="page-btn"
					disabled={currentPage === 1}
					on:click={() => goToPage(currentPage - 1)}
				>
					Previous
				</button>

				<div class="page-numbers">
					{#each Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
						const start = Math.max(1, currentPage - 2);
						return start + i;
					}) as page}
						{#if page <= totalPages}
							<button
								class="page-num"
								class:active={page === currentPage}
								on:click={() => goToPage(page)}
							>
								{page}
							</button>
						{/if}
					{/each}
				</div>

				<button
					class="page-btn"
					disabled={currentPage === totalPages}
					on:click={() => goToPage(currentPage + 1)}
				>
					Next
				</button>
			</div>
		{/if}
	{/if}
</div>

<style>
	.users-container {
		margin-bottom: 2rem;
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
		padding: 1.5rem;
	}

	.users-header {
		margin-bottom: 1.5rem;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.users-header h2 {
		font-size: 1.25rem;
		font-weight: 600;
		margin: 0;
		color: var(--text-primary, #e6edf3);
	}

	.total-count {
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
		margin: 0;
	}

	.users-table-container {
		overflow-x: auto;
	}

	.users-table {
		width: 100%;
		border-collapse: collapse;
	}

	.users-table thead th {
		text-align: left;
		padding: 0.75rem;
		font-size: 0.875rem;
		font-weight: 600;
		color: var(--text-secondary, #8b949e);
		border-bottom: 1px solid var(--border-primary, #30363d);
	}

	.users-table tbody td {
		padding: 0.75rem;
		border-bottom: 1px solid var(--border-secondary, #21262d);
		color: var(--text-primary, #e6edf3);
		font-size: 0.875rem;
	}

	.users-table tbody tr:hover {
		background: var(--bg-tertiary, #1c2128);
	}

	.user-cell {
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.user-avatar {
		width: 32px;
		height: 32px;
		border-radius: 50%;
		object-fit: cover;
	}

	.user-avatar-placeholder {
		width: 32px;
		height: 32px;
		border-radius: 50%;
		background: var(--accent-primary, #238636);
		display: flex;
		align-items: center;
		justify-content: center;
		font-weight: 600;
		color: #ffffff;
	}

	.username {
		font-weight: 500;
	}

	.email {
		color: var(--text-secondary, #8b949e);
	}

	.role-badge {
		padding: 0.25rem 0.75rem;
		border-radius: 12px;
		font-size: 0.75rem;
		font-weight: 500;
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-secondary, #8b949e);
		text-transform: capitalize;
	}

	.role-badge.admin {
		background: rgba(88, 166, 255, 0.1);
		color: #58a6ff;
	}

	.status-badge {
		padding: 0.25rem 0.75rem;
		border-radius: 12px;
		font-size: 0.75rem;
		font-weight: 500;
		background: rgba(248, 81, 73, 0.1);
		color: #f85149;
	}

	.status-badge.active {
		background: rgba(126, 184, 69, 0.1);
		color: #7eb845;
	}

	.pagination {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.5rem;
		margin-top: 1.5rem;
		padding-top: 1.5rem;
		border-top: 1px solid var(--border-primary, #30363d);
	}

	.page-btn {
		padding: 0.5rem 1rem;
		border: 1px solid var(--border-primary, #30363d);
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
		border-radius: 6px;
		cursor: pointer;
		font-size: 0.875rem;
		font-weight: 500;
		transition: all 0.2s ease;
	}

	.page-btn:hover:not(:disabled) {
		background: var(--accent-primary, #238636);
		border-color: var(--accent-primary, #238636);
		color: #ffffff;
	}

	.page-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.page-numbers {
		display: flex;
		gap: 0.25rem;
	}

	.page-num {
		padding: 0.5rem 0.75rem;
		border: 1px solid var(--border-primary, #30363d);
		background: var(--bg-tertiary, #1c2128);
		color: var(--text-primary, #e6edf3);
		border-radius: 6px;
		cursor: pointer;
		font-size: 0.875rem;
		font-weight: 500;
		transition: all 0.2s ease;
		min-width: 40px;
		text-align: center;
	}

	.page-num:hover {
		background: var(--bg-primary, #0d1117);
	}

	.page-num.active {
		background: var(--accent-primary, #238636);
		border-color: var(--accent-primary, #238636);
		color: #ffffff;
	}

	.loading,
	.error {
		padding: 2rem;
		text-align: center;
	}

	.error {
		color: var(--danger, #f85149);
	}
</style>
